import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { ChevronRight, Plus, Users } from "@fleet/icons";
import { Link } from "@tanstack/solid-router";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Skeleton, TextField } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { type components, queryKeys } from "@stockpilot/shared";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useActiveOrg } from "@/features/organizations/hooks/use-active-org";
import { QueryErrorState } from "@/components/ui/query-error-state";

export function OrganizationList() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const ctx = useActiveOrg();
  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const [adding, setAdding] = createSignal(false);
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const canSubmit = () => Boolean(db()) && name().trim().length > 0 && !busy();

  const resetForm = () => {
    setAdding(false);
    setName("");
    setError(null);
  };

  // Organization creation (company/supermarket): reuses `bootstrapOrg` (atomic
  // server-side creation + wait for sync + activation as active org). Error
  // inline via `handleApiResponse` (CC-30): never leave button stuck without
  // feedback (e.g. a visitor receives 401 and will see it here instead of hanging).
  async function createOrg(e: Event) {
    e.preventDefault();
    if (!canSubmit()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ctx.bootstrapOrg(name());
      resetForm();
      // The API list is not local-first; we refresh it after creation (the
      // switcher, reading from local PowerSync, already updated itself).
      void orgs.refetch();
    } catch (err) {
      console.error("[OrganizationList] create organization failed:", err);
      setError(t(msg`We couldn't create your organization. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold tracking-tight">{t(msg`Organizations`)}</h2>
        <Button
          size="sm"
          class="gap-2"
          data-testid="org-list-new"
          onClick={() => setAdding((v) => !v)}
        >
          <Plus class="h-4 w-4" />
          {t(msg`New`)}
        </Button>
      </div>

      <Show when={adding()}>
        <Card>
          <CardContent class="p-4">
            <form class="space-y-3" onSubmit={createOrg}>
              <TextField
                data-testid="org-list-name-input"
                placeholder={t(msg`Company name`)}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                autofocus
              />
              <div class="flex gap-2">
                <Button
                  data-testid="org-list-create"
                  type="submit"
                  size="sm"
                  disabled={!canSubmit()}
                >
                  {busy() ? t(msg`Creating…`) : t(msg`Create organization`)}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={resetForm}
                  disabled={busy()}
                >
                  {t(msg`Cancel`)}
                </Button>
              </div>
              <Show when={error()}>
                {(message) => (
                  <p role="alert" data-testid="org-list-error" class="text-sm text-destructive">
                    {message()}
                  </p>
                )}
              </Show>
            </form>
          </CardContent>
        </Card>
      </Show>

      {/* P23 / CC-60 · I3 — "failed to load" is not "nothing here". With the API
          down `isLoading` is FALSE and `data` does not exist, so the guard below
          let it pass and the screen rendered its EMPTY state. We WRAP the entire
          block: chaining it to the existing guard would render the error AND the
          fallback skeleton at the same time, forever. */}
      <Show when={orgs.isError}>
        <QueryErrorState testId="organizations-error" compact onRetry={() => void orgs.refetch()} />
      </Show>
      <Show when={!orgs.isError}>
        <Show
          when={!orgs.isLoading}
          fallback={
            <div class="space-y-4">
              <For each={Array.from({ length: 3 })}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <Show
            when={orgs.data && orgs.data.length > 0}
            fallback={
              <div class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-12 text-center">
                <Users class="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
                <h3 class="text-lg font-semibold">{t(msg`No organizations`)}</h3>
                <p class="text-sm text-muted-foreground">
                  {t(msg`Join one or create your own to collaborate.`)}
                </p>
              </div>
            }
          >
            <div class="grid gap-4">
              <For each={orgs.data}>
                {(org) => (
                  <Link to="/organizations/$orgId" params={{ orgId: org.id }} class="group block">
                    <Card class="transition-[border-color,box-shadow] duration-200 ease-out hover:border-primary/50 hover:shadow-md">
                      <CardContent class="flex items-center gap-4 p-4">
                        <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Users class="h-6 w-6" />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center gap-2">
                            <h3 class="font-bold truncate">{org.name}</h3>
                            <Badge variant="outline" class="text-[10px]">
                              @{org.slug}
                            </Badge>
                          </div>
                          <p class="text-xs text-muted-foreground">
                            {(() => {
                              const id = org.id.slice(0, 8);
                              return t(msg`ID: ${id}...`);
                            })()}
                          </p>
                        </div>
                        <ChevronRight class="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </CardContent>
                    </Card>
                  </Link>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}
